// The one map from a contract-model date's computed status to what a person
// reads (wireframe 1). The state itself is pangaea's (occurrencestatus.Of on
// the request's as_of day); this file only names it. Every surface that shows
// a deliverable date's state reads its label and tone from here, so a date
// never reads two ways on two pages.
import { cmDate, daysBetween, todayISO } from './format';
/** The occurrencestatus states, in the order a queue sorts them. */ export const OCCURRENCE_STATES = [
    'late',
    'rejected',
    'awaiting_review',
    'due_soon',
    'upcoming',
    'due_unknown',
    'waiting_on_event',
    'held',
    'approved',
    'waived',
    'not_owed',
    'retired'
];
/** Short labels for counts (KPI tiles, filters): "Late 2". */ export const STATE_SHORT_LABEL = {
    late: 'Late',
    rejected: 'Rejected',
    awaiting_review: 'Awaiting review',
    due_soon: 'Due in 14 days',
    upcoming: 'Upcoming',
    due_unknown: 'Due date unknown',
    waiting_on_event: 'Waiting on an event',
    held: 'Held',
    approved: 'Approved',
    waived: 'Waived by the COR',
    not_owed: 'Not owed',
    retired: 'Retired'
};
export const STATE_TONE = {
    late: 'danger',
    rejected: 'danger',
    awaiting_review: 'info',
    due_soon: 'warning',
    upcoming: 'neutral',
    due_unknown: 'neutral',
    waiting_on_event: 'info',
    held: 'success',
    approved: 'success',
    waived: 'neutral',
    not_owed: 'neutral',
    retired: 'neutral'
};
/** Review tiers (000213's CHECK), in the order a reviewer picks them. */ export const REVIEW_TIERS = [
    'va_approver',
    'va_pm',
    'va_cor',
    'contractor',
    'unspecified'
];
const TIER_LABEL = {
    va_approver: 'VA Approver',
    va_pm: 'VA PM',
    va_cor: 'VA COR',
    contractor: 'Contractor',
    unspecified: 'Unspecified'
};
/** "VA PM" for va_pm; an unknown tier reads as itself, never blank. */ export function tierLabel(tier) {
    if (!tier) return TIER_LABEL.va_pm;
    return TIER_LABEL[tier] ?? tier;
}
export function isOccurrenceState(s) {
    return OCCURRENCE_STATES.includes(s);
}
function plural(n, one, many = `${one}s`) {
    return `${n} ${n === 1 ? one : many}`;
}
/**
 * The status line for one date: "Late", "Due in 4 days", "Awaiting VA PM
 * review", "Rejected – resubmit", "Approved, 37 days late", "Waived by the
 * COR", "Not owed", "Due date unknown", "Held 15 Oct 2024, 2 business days
 * late", "Projected · waiting on Technical Kickoff Meeting".
 *
 * `finalTier` is the deliverable's final review tier (the review a filing is
 * waiting on). `today` is injectable for tests.
 */ export function occurrenceStatus(occ, finalTier, today = todayISO()) {
    const st = occ.status;
    const state = st?.state ?? '';
    const tone = isOccurrenceState(state) ? STATE_TONE[state] : 'neutral';
    const ev = occ.event;
    switch(state){
        case 'waiting_on_event':
            return {
                state,
                tone,
                label: `Projected · waiting on ${ev?.name || 'an event'}`
            };
        case 'held':
            {
                const on = ev?.heldOn ? ` ${cmDate(ev.heldOn)}` : '';
                const late = ev?.businessDaysLate ?? 0;
                return late > 0 ? {
                    state,
                    tone: 'warning',
                    label: `Held${on}, ${plural(late, 'business day')} late`
                } : {
                    state,
                    tone,
                    label: `Held${on}`
                };
            }
        case 'late':
            return {
                state,
                tone,
                label: st?.daysLate ? `Late, ${plural(st.daysLate, 'day')}` : 'Late'
            };
        case 'due_soon':
        case 'upcoming':
            {
                const n = occ.dueOn ? daysBetween(today, occ.dueOn) : null;
                if (n === 0) return {
                    state,
                    tone,
                    label: 'Due today'
                };
                return {
                    state,
                    tone,
                    label: n === null ? 'Upcoming' : `Due in ${plural(n, 'day')}`
                };
            }
        case 'awaiting_review':
            return {
                state,
                tone,
                label: `Awaiting ${tierLabel(finalTier)} review`
            };
        case 'rejected':
            return {
                state,
                tone,
                label: 'Rejected – resubmit'
            };
        case 'approved':
            {
                if (st?.autoApproved) return {
                    state,
                    tone,
                    label: 'Approved (review window lapsed)'
                };
                if (!st?.timingKnown) return {
                    state,
                    tone,
                    label: 'Approved'
                };
                if (st.onTime) return {
                    state,
                    tone,
                    label: 'Approved on time'
                };
                return {
                    state,
                    tone: 'warning',
                    label: `Approved, ${plural(st.daysLate, 'day')} late`
                };
            }
        case 'waived':
            return {
                state,
                tone,
                label: 'Waived by the COR'
            };
        case 'not_owed':
            return {
                state,
                tone,
                label: 'Not owed'
            };
        case 'due_unknown':
            return {
                state,
                tone,
                label: 'Due date unknown'
            };
        case 'retired':
            return {
                state,
                tone,
                label: 'Retired'
            };
        default:
            return {
                state,
                tone: 'neutral',
                label: state || 'Unknown'
            };
    }
}
/** Open = still owed and not yet approved (a queue row). */ export function isOpenState(state) {
    return [
        'late',
        'rejected',
        'awaiting_review',
        'due_soon',
        'upcoming',
        'due_unknown'
    ].includes(state);
}
/**
 * The on-time share from ListOccurrences' named denominators: approved on time
 * over approved with timing known. null when nothing is known yet — a KPI must
 * not print 0% for "no approvals".
 */ export function onTimePercent(counts) {
    if (!counts || counts.timingKnown === 0) return null;
    return Math.round(counts.onTime / counts.timingKnown * 100);
}
export const HANDLING_LABEL = {
    performed: 'Performed',
    routed: 'Routed',
    not_owed: 'Not owed'
};
export const NOT_OWED_REASON_LABEL = {
    waived: 'Waived by the COR',
    not_applicable: 'Does not apply',
    duplicate: 'Duplicate',
    not_listed: 'Not listed in this SOW'
};
export const SOW_STATE = {
    approved_waiting: {
        label: 'Approved, waiting',
        tone: 'neutral'
    },
    in_force: {
        label: 'In force',
        tone: 'success'
    },
    ended: {
        label: 'Ended',
        tone: 'neutral'
    }
};
